import { useGT, useLocale } from 'gt-react';
import {
  dictionaryKeys as keys,
  registerLanguageDictionary,
  type LanguageDictionary,
} from 'handsontable/i18n/registry';

type GT = ReturnType<typeof useGT>;

/**
 * Handsontable's column filter menu (DropdownMenu + Filters) in the current
 * gt-react locale. Keys left out — the context menu's, for one — fall back to
 * Handsontable's own en-US text.
 */
function handsontableFilterDictionary(
  gt: GT,
  languageCode: string,
): LanguageDictionary {
  const condition = 'spreadsheet column filter condition';
  const button = 'button in a spreadsheet column filter menu';
  return {
    languageCode,
    [keys.FILTERS_CONDITIONS_NONE]: gt('None', { context: condition }),
    [keys.FILTERS_CONDITIONS_EMPTY]: gt('Is empty', { context: condition }),
    [keys.FILTERS_CONDITIONS_NOT_EMPTY]: gt('Is not empty', {
      context: condition,
    }),
    [keys.FILTERS_CONDITIONS_EQUAL]: gt('Is equal to', { context: condition }),
    [keys.FILTERS_CONDITIONS_NOT_EQUAL]: gt('Is not equal to', {
      context: condition,
    }),
    [keys.FILTERS_CONDITIONS_BEGINS_WITH]: gt('Begins with', {
      context: condition,
    }),
    [keys.FILTERS_CONDITIONS_ENDS_WITH]: gt('Ends with', {
      context: condition,
    }),
    [keys.FILTERS_CONDITIONS_CONTAINS]: gt('Contains', { context: condition }),
    [keys.FILTERS_CONDITIONS_NOT_CONTAIN]: gt('Does not contain', {
      context: condition,
    }),
    [keys.FILTERS_CONDITIONS_GREATER_THAN]: gt('Greater than', {
      context: condition,
    }),
    [keys.FILTERS_CONDITIONS_GREATER_THAN_OR_EQUAL]: gt(
      'Greater than or equal to',
      { context: condition },
    ),
    [keys.FILTERS_CONDITIONS_LESS_THAN]: gt('Less than', {
      context: condition,
    }),
    [keys.FILTERS_CONDITIONS_LESS_THAN_OR_EQUAL]: gt('Less than or equal to', {
      context: condition,
    }),
    [keys.FILTERS_CONDITIONS_BETWEEN]: gt('Is between', { context: condition }),
    [keys.FILTERS_CONDITIONS_NOT_BETWEEN]: gt('Is not between', {
      context: condition,
    }),
    [keys.FILTERS_CONDITIONS_AFTER]: gt('After', {
      context: `${condition}, a date after the given one`,
    }),
    [keys.FILTERS_CONDITIONS_BEFORE]: gt('Before', {
      context: `${condition}, a date before the given one`,
    }),
    [keys.FILTERS_CONDITIONS_TODAY]: gt('Today', { context: condition }),
    [keys.FILTERS_CONDITIONS_TOMORROW]: gt('Tomorrow', { context: condition }),
    [keys.FILTERS_CONDITIONS_YESTERDAY]: gt('Yesterday', {
      context: condition,
    }),
    [keys.FILTERS_VALUES_BLANK_CELLS]: gt('Blank cells', {
      context: 'spreadsheet column filter value list entry for empty cells',
    }),
    [keys.FILTERS_DIVS_FILTER_BY_CONDITION]: gt('Filter by condition', {
      context: 'spreadsheet column filter menu section heading',
    }),
    [keys.FILTERS_DIVS_FILTER_BY_VALUE]: gt('Filter by value', {
      context: 'spreadsheet column filter menu section heading',
    }),
    [keys.FILTERS_LABELS_CONJUNCTION]: gt('And', {
      context: 'spreadsheet column filter, both conditions must match',
    }),
    [keys.FILTERS_LABELS_DISJUNCTION]: gt('Or', {
      context: 'spreadsheet column filter, either condition may match',
    }),
    [keys.FILTERS_BUTTONS_SELECT_ALL]: gt('Select all', {
      context: `${button}, checks every value`,
    }),
    [keys.FILTERS_BUTTONS_CLEAR]: gt('Clear', {
      context: `${button}, unchecks every value`,
    }),
    [keys.FILTERS_BUTTONS_OK]: gt('OK', {
      context: `${button}, applies the filter`,
    }),
    [keys.FILTERS_BUTTONS_CANCEL]: gt('Cancel', { context: button }),
    [keys.FILTERS_BUTTONS_PLACEHOLDER_SEARCH]: gt('Search', {
      context: 'placeholder for searching a spreadsheet column filter value',
    }),
    [keys.FILTERS_BUTTONS_PLACEHOLDER_VALUE]: gt('Value', {
      context: 'placeholder for a spreadsheet column filter condition value',
    }),
    [keys.FILTERS_BUTTONS_PLACEHOLDER_SECOND_VALUE]: gt('Second value', {
      context:
        'placeholder for the upper bound of an "is between" spreadsheet filter',
    }),
  };
}

/**
 * The `language` to pass a Handsontable grid so its built-in menus read in the
 * user's locale through our own catalog, not Handsontable's language packs
 * (which have no fr-CA or en-CA).
 *
 * The code is namespaced so it never replaces Handsontable's default en-US
 * dictionary, which fills in every key this one leaves out. Registration
 * happens during render: the grid reads the dictionary when it mounts, which
 * is before any effect of its parent runs. It's idempotent, so a re-render
 * that registers again is harmless.
 */
export function useHandsontableLanguage(): string {
  const gt = useGT();
  const locale = useLocale();
  const languageCode = `gently-${locale}`;
  registerLanguageDictionary(handsontableFilterDictionary(gt, languageCode));
  return languageCode;
}
